import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  X, 
  FileText, 
  Mail, 
  Image, 
  MessageSquare,
  ArrowRight,
  Eye,
  Rocket,
  Lock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { DEMO_FUNNEL_DATA } from '@/config/briefTemplates';

/**
 * Demo Funnel Viewer - Read-only view of a complete funnel
 * Shows the end result to help users understand what they'll create
 */
const DemoFunnelViewer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('landing');
  
  if (!isOpen) return null;

  const demo = DEMO_FUNNEL_DATA;
  const brief = demo.brief;

  const tabs = [
    { id: 'landing', label: 'Landing', icon: FileText },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'copies', label: 'Copies Ads', icon: MessageSquare },
    { id: 'creatives', label: 'Creativos', icon: Image },
  ];

  const handleCreateMyFunnel = () => {
    onClose();
    navigate('/funnel/new');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-border">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-violet-500 flex items-center justify-center">
                <Eye className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
                    {demo.name}
                  </h2>
                  <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium rounded-full flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Solo lectura
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Ejemplo de funnel completo generado con FASCINAI
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors p-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-brand-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {activeTab === 'landing' && (
            <LandingPreview landing={demo.assets.landingCaptacion} brief={brief} />
          )}
          {activeTab === 'emails' && (
            <EmailsPreview emails={demo.assets.emailsCaptacion} />
          )}
          {activeTab === 'copies' && (
            <CopiesPreview copies={demo.assets.adsCopyCaptacion} />
          )}
          {activeTab === 'creatives' && (
            <CreativesPreview creatives={demo.assets.creativos} />
          )}
        </div>

        {/* Footer CTA */}
        <div className="p-4 sm:p-6 border-t border-border bg-gradient-to-r from-brand-50 to-violet-50 dark:from-brand-900/20 dark:to-violet-900/20">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-foreground text-center sm:text-left">
              <strong>¿Te gustaría crear algo así?</strong> Tu primer funnel completo en ~5 minutos.
            </p>
            <Button
              onClick={handleCreateMyFunnel}
              className="bg-gradient-to-r from-brand-500 to-violet-500 hover:from-brand-600 hover:to-violet-600 text-white px-6"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Crear mi funnel
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Landing page preview
const LandingPreview = ({ landing, brief }) => (
  <div className="space-y-6">
    <div className="bg-gradient-to-br from-brand-500 to-violet-600 rounded-2xl p-8 text-white">
      <span className="px-3 py-1 bg-white/20 rounded-full text-sm mb-4 inline-block">
        {brief.expert_name || 'Expert Name'}
      </span>
      <h1 className="font-display text-2xl sm:text-3xl font-bold mb-4">
        {brief.main_promise}
      </h1>
      <p className="text-white/90 mb-6 max-w-2xl">
        {brief.product_description?.slice(0, 150)}...
      </p>
      <button className="bg-white text-brand-600 px-6 py-3 rounded-xl font-semibold hover:bg-white/90 transition-colors">
        Reserva tu plaza
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-muted/50 rounded-xl p-4 border border-border">
        <h3 className="font-semibold text-foreground mb-2">Producto</h3>
        <p className="text-sm text-muted-foreground">{brief.product_name}</p>
      </div>
      <div className="bg-muted/50 rounded-xl p-4 border border-border">
        <h3 className="font-semibold text-foreground mb-2">Precio</h3>
        <p className="text-sm text-muted-foreground">{brief.price} {brief.currency}</p>
      </div>
      <div className="bg-muted/50 rounded-xl p-4 border border-border">
        <h3 className="font-semibold text-foreground mb-2">Garantía</h3>
        <p className="text-sm text-muted-foreground">{brief.dias_garantia} días</p>
      </div>
    </div>

    <div className="bg-muted/30 rounded-xl p-6 border border-border">
      <h3 className="font-semibold text-foreground mb-3">Puntos de dolor que resuelve:</h3>
      <p className="text-muted-foreground text-sm">{brief.pain_points}</p>
    </div>
  </div>
);

// Emails preview
const EmailsPreview = ({ emails }) => {
  const [activeEmail, setActiveEmail] = useState(0);
  const email = emails[activeEmail];

  return (
    <div className="space-y-4">
      {/* Email selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {emails.map((e, i) => (
          <button
            key={e.id}
            onClick={() => setActiveEmail(i)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeEmail === i
                ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-800'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Email {i + 1}
          </button>
        ))}
      </div>

      {/* Email preview */}
      <div className="bg-white dark:bg-background border border-border rounded-xl overflow-hidden">
        <div className="bg-muted/50 px-4 py-3 border-b border-border">
          <p className="text-xs text-muted-foreground">Asunto:</p>
          <p className="font-medium text-foreground">{email.subject}</p>
        </div>
        <div className="p-4">
          <pre className="whitespace-pre-wrap text-sm text-foreground font-sans">
            {email.content}
          </pre>
        </div>
      </div>
    </div>
  );
};

// Ad copies preview
const CopiesPreview = ({ copies }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {copies.map((copy) => (
      <div key={copy.id} className="bg-white dark:bg-background border border-border rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-brand-500 to-violet-500 px-4 py-2">
          <span className="text-white text-xs font-medium">
            Ángulo: {copy.angulo}
          </span>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Headline</label>
            <p className="font-semibold text-foreground">{copy.headline}</p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Texto principal</label>
            <p className="text-sm text-muted-foreground">{copy.primary_text}</p>
          </div>
          <div className="pt-2">
            <span className="px-3 py-1 bg-brand-500 text-white text-sm rounded-lg">
              {copy.cta}
            </span>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Creatives preview
const CreativesPreview = ({ creatives }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {creatives.map((creative) => (
      <div key={creative.id} className="bg-white dark:bg-background border border-border rounded-xl overflow-hidden">
        <div className="aspect-[4/5] bg-muted relative">
          <img 
            src={creative.asset_url} 
            alt={creative.headline}
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <p className="text-white font-semibold">{creative.headline}</p>
          </div>
        </div>
        <div className="p-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Formato: {creative.formato}
          </span>
          <span className="text-xs bg-muted px-2 py-1 rounded">
            {creative.motor}
          </span>
        </div>
      </div>
    ))}
  </div>
);

export default DemoFunnelViewer;
